import dayjs from "dayjs"
import weekOfYear from "dayjs/plugin/weekOfYear";
import isoWeek from "dayjs/plugin/isoWeek";
import { TestCompliance } from "../../waterline-management/types"
import { BioCalendarRow, BioCalendarSeed, BioCalendarTestWeek, BioCalendarWeekDetails, BioChild, BioChildStatusCount, BioCustomer, BioTestResultByWeek, ChildResultsByWeek } from "../types"
import { ComplianceStatus, Groups } from "../../../../types";

dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);

const getMonths = (fromDate: string, toDate: string) => {
  const from = dayjs(fromDate)
  const to = dayjs(toDate)

  let months = []
  let monthsItr = dayjs(from).clone()
  const boundry = dayjs(to).clone().endOf('month')

  const peek = dayjs(from).clone().add(1, 'months').startOf('month')
  const peekFrom = dayjs(from).clone()
  if (peek.diff(peekFrom, 'weeks') < 2) {
    monthsItr.add(1, 'months')
  }

  do {
    const date = dayjs(monthsItr).toDate()
    months.push(`${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear().toString().slice(-2)}`)
    monthsItr = monthsItr.add(1, 'months')
  } while (monthsItr.isBefore(boundry))

  return months
}

const getBioCalendarSeeds = (first: BioCalendarTestWeek, last: BioCalendarTestWeek) => {
  const DEFAULT_START_WEEK = 1
  const WEEKS_IN_YEAR = 52

  const years: number[] = []
  
  let currentYear: number = first.year

  while (currentYear <= last.year) {
    years.push(currentYear)
    ++currentYear
  }

  const count = years.length

  const seeds = years.map((year, index) => {
    const hasOneYear = count === 1

    const isFirstYear = index === 0
    const isLastYear = index + 1 === count

    const start = isFirstYear ? first.week : DEFAULT_START_WEEK
    const end = (hasOneYear || isLastYear) ? last.week : WEEKS_IN_YEAR

    const seed: BioCalendarSeed = { start, end, year }

    return seed
  })  

  return seeds
}

const getCalendarWeeks = (bioCustomer: BioCustomer) => {
  const { fromDate, toDate } = bioCustomer

  const range = {
    from: dayjs(fromDate),
    to: dayjs(toDate),
  }

  const first: BioCalendarTestWeek = {
    week: range.from.isoWeek(),
    year: range.from.isoWeekYear(),
  }

  const last: BioCalendarTestWeek = {
    week: range.to.isoWeek(),
    year: range.to.isoWeekYear(),
  }

  const seeds = getBioCalendarSeeds(first, last)

  const calendarWeeks: BioCalendarTestWeek[] = []

  seeds.forEach(seed => {
    let currentWeek = seed.start

    while (currentWeek <= seed.end) {
      const week: BioCalendarTestWeek = { week: currentWeek, year: seed.year }
      calendarWeeks.push(week)
      ++currentWeek
    }
  })

  return calendarWeeks
}

const getCalendarWeekDetails = (calendarWeek: BioCalendarTestWeek) => {
  const isoWeek = dayjs().year(calendarWeek.year).isoWeek(calendarWeek.week)

  const details: BioCalendarWeekDetails = {
    key: `${calendarWeek.year}-${calendarWeek.week}`,
    start: isoWeek.startOf('isoWeek'),
    end: isoWeek.endOf('isoWeek'),
  }

  return details
}

const getIsMissedWeek = (child: BioChild, weekDetails: BioCalendarWeekDetails) => {
  const now = dayjs()

  const { bio_first_test_date } = child    

  const firstTestDate = bio_first_test_date ? dayjs(bio_first_test_date) : null
  const isActiveTester = Boolean(bio_first_test_date)

  const isMissedWeek = isActiveTester && firstTestDate
    && !weekDetails.end.isAfter(now) && weekDetails.start.isAfter(firstTestDate)

  return isMissedWeek
}

const getWeekResult = (child: BioChild, calendarWeek: BioCalendarTestWeek) => {
  const { testResultsByWeek = {} } = child  
  const weekDetails = getCalendarWeekDetails(calendarWeek)
  const weekResult = { key: weekDetails.key } as BioTestResultByWeek
  const testWeek = testResultsByWeek[weekDetails.key]

  if (testWeek) {
    const { dateRange, results, isPassing, isInvalid } = testWeek

    if (!isPassing && !isInvalid) {
      weekResult.message = TestCompliance.failedTest
      weekResult.status = ComplianceStatus.red
    } else if (!isPassing && isInvalid) {
      weekResult.message = TestCompliance.invalidTest
      weekResult.status = ComplianceStatus.red
    } else {
      weekResult.message = TestCompliance.passedTest
      weekResult.status = ComplianceStatus.green
    }

    weekResult.dateRange = dateRange
    weekResult.results = results
  } else {
    const isMissedWeek = getIsMissedWeek(child, weekDetails)

    const status = isMissedWeek
      ? ComplianceStatus.yellow
      : ComplianceStatus.gray

    const dateRange = `${weekDetails.start.format('L')} to ${weekDetails.end.format('L')}`
    const message = isMissedWeek ? TestCompliance.missedTest : TestCompliance.inactive

    weekResult.dateRange = dateRange
    weekResult.status = status
    weekResult.message = message
    weekResult.results = []
  }

  return weekResult
}

const getChildResultsByWeek = (child: BioChild, calendarWeeks: BioCalendarTestWeek[]) => {
  const weekResults = calendarWeeks.map(calendarWeek => {
    return getWeekResult(child, calendarWeek)
  })

  const testResultsByWeek = weekResults.reduce((resultsByWeek: ChildResultsByWeek, weekResult) => {
    resultsByWeek[weekResult.key] = weekResult
    return resultsByWeek
  }, {})

  return testResultsByWeek
}

const getWeekStatus = (child: BioChild, calendarWeek: BioCalendarTestWeek) => {
  const { testResultsByWeek = {} } = child  
  const weekDetails = getCalendarWeekDetails(calendarWeek)
  const testWeek = testResultsByWeek[weekDetails.key]
  const weekStatus: BioChildStatusCount = { missed: 0, notStarted: 0 }

  if (!testWeek) {
    const isMissedWeek = getIsMissedWeek(child, weekDetails)

    isMissedWeek ? ++weekStatus.missed : ++weekStatus.notStarted
  }
  
  return weekStatus
}

const getChildCompliance = (child: BioChild, calendarWeeks: BioCalendarTestWeek[]) => {  
  let total = calendarWeeks.length

  const childStatusCount = calendarWeeks.reduce((statusCount: BioChildStatusCount, calendarWeek) => {
    const weekStatus = getWeekStatus(child, calendarWeek)

    statusCount.missed += weekStatus.missed
    statusCount.notStarted += weekStatus.notStarted

    return statusCount
  }, { missed: 0, notStarted: 0 })

  const testedWeeks = total - childStatusCount.notStarted - childStatusCount.missed
  const testableWeeks = total - childStatusCount.notStarted

  const compliance = testableWeeks > 0
    ? Math.round((testedWeeks / testableWeeks) * 100)
    : 0

  return compliance
}

const getCalendarData = (bioCustomer: BioCustomer) => {
  const { children } = bioCustomer

  const calendarWeeks = getCalendarWeeks(bioCustomer)

  const calendarRows = children.map(child => {
    const {
      customer_number = '',
      name = '',
      city = '',
      state = '',
      doctor_name = '',
    } = child

    const childCalendarData: BioCalendarRow = {
      customerNumber: customer_number,
      practiceName: name.trim(),
      practiceCity: city.trim(),
      practiceState: state.trim(),
      doctor_name: doctor_name.trim(),
      testResultsByWeek: {},
      compliance: { totalStatus: 0 }
    };

    childCalendarData.testResultsByWeek = getChildResultsByWeek(child, calendarWeeks)
    childCalendarData.compliance.totalStatus = getChildCompliance(child, calendarWeeks)

    return childCalendarData;
  })

  return calendarRows.sort((a, b) => b.compliance.totalStatus - a.compliance.totalStatus);
};

const getFilteredPractices = (search: string, filterByType: string, filterByGroup: Groups[], practices: BioCalendarRow[]) => {
  let filtered: BioCalendarRow[] = structuredClone(practices)

  let membersSet: Set<string> | undefined;
  if (filterByGroup.length) {
    const members = filterByGroup.flatMap((group) => group?.members.map((member) => member.customer_number));
    if (members) {
      membersSet = new Set(members);
    }
  }

  const cleanSearch = search?.trim().toLowerCase();

  filtered = filtered.filter(practice => {
    if (membersSet && !membersSet.has(practice.customerNumber)) return false;

    if (cleanSearch) {
      const practiceName = practice.practiceName.trim().toLowerCase();
      const customerNumber = practice.customerNumber.trim();
      if (!practiceName.includes(cleanSearch) && !customerNumber.includes(cleanSearch)) return false;
    }

    if (filterByType && filterByType !== 'All results') {
      const testResults = Object.values(practice.testResultsByWeek);
      if (!testResults.some((result: BioTestResultByWeek) => result.message === filterByType)) return false;
    }

    return true;
  });

  return filtered;
}

export {
  getMonths,
  getCalendarData,
  getFilteredPractices
}